import RemoteImage from "./RemoteImage"
import { ITEM_KIND, NA } from "../models/menu"
import {
  isAllergenMatch,
  isDietSelection,
  orderByMatch,
  splitAllergens
} from "../models/allergens"
import "./MenuList.css"

// Rows that fit on an iPad before any scrolling; their images load eagerly.
const ABOVE_FOLD_ROWS = 6

export default function MenuList({
  items,
  categoryHeader,
  selectedAllergenIds,
  showAllergens,
  onSelectItem
}) {
  return (
    <ul className="menu-list">
      {categoryHeader && (
        <li className="menu-list__header">
          <CategoryHeaderCard category={categoryHeader} />
        </li>
      )}
      {items.map((item, index) => (
        <li key={`${item.kind}-${item.id || index}`}>
          <MenuRow
            item={item}
            isLast={index === items.length - 1}
            isAboveFold={index < ABOVE_FOLD_ROWS}
            selectedAllergenIds={selectedAllergenIds}
            showAllergens={showAllergens}
            onSelect={() => onSelectItem(item)}
          />
        </li>
      ))}
    </ul>
  )
}

function MenuRow({ item, isLast, isAboveFold, selectedAllergenIds, showAllergens, onSelect }) {
  const isFiltering = item.kind === ITEM_KIND.FOOD && selectedAllergenIds.length > 0

  return (
    <>
      <button
        type="button"
        className={`menu-row menu-row--${item.kind}${isFiltering ? " menu-row--framed" : ""}`}
        data-allergen-mode={isFiltering ? (showAllergens ? "with" : "without") : undefined}
        data-filter-kind={isFiltering && isDietSelection(selectedAllergenIds) ? "diet" : undefined}
        onClick={onSelect}
      >
        <RemoteImage
          className="menu-row__image"
          src={item.thumbnailUrl || item.imageUrl}
          fallbackSrc={item.imageUrl}
          alt=""
          isPriority={isAboveFold}
        />
        <div className="menu-row__body">
          <div className="menu-row__heading">
            {/* A translatable field can come back null; never leave the row untitled. */}
            <span className="menu-row__title t-sb18">{item.name || NA}</span>
            {item.price && <span className="menu-row__price t-s14">{item.price}</span>}
          </div>
          <RowDetails
            item={item}
            selectedAllergenIds={selectedAllergenIds}
            isFiltering={isFiltering}
          />
        </div>
      </button>
      {!isLast && <div className="menu-row__separator" />}
    </>
  )
}

function RowDetails({ item, selectedAllergenIds, isFiltering }) {
  switch (item.kind) {
    case ITEM_KIND.FOOD:
      return (
        <>
          <p className="menu-row__line t-p12">
            {item.description ? (
              <span className="menu-row__value">{item.description}</span>
            ) : (
              <LabelledValue label="Menu Description: " value={NA} />
            )}
          </p>
          <p className="menu-row__line t-p12">
            <AllergenValue
              value={item.allergens}
              selectedAllergenIds={isFiltering ? selectedAllergenIds : []}
            />
          </p>
          <p className="menu-row__line t-p12">
            {item.hasChefDescription && <span className="menu-row__label">Chef's Description: </span>}
            <span className="menu-row__more">Click Here...</span>
          </p>
        </>
      )

    case ITEM_KIND.WINE:
      return (
        <>
          <p className="menu-row__line t-p12">
            <span className="menu-row__value">{item.origin}</span>
          </p>
          <p className="menu-row__line t-p12">
            <LabelledValue label="Varietals: " value={item.varietals} />
          </p>
          <p className="menu-row__line t-p12">
            <span className="menu-row__more">Read More...</span>
          </p>
        </>
      )

    case ITEM_KIND.BAR:
      return (
        <>
          {item.lines.map((line) => (
            <p className="menu-row__line t-p12" key={line.label}>
              <LabelledValue label={line.label} value={line.value} />
            </p>
          ))}
          <p className="menu-row__line t-p12">
            <span className="menu-row__more">Read More...</span>
          </p>
        </>
      )

    case ITEM_KIND.SPIRIT:
      return (
        <>
          <p className="menu-row__line t-p12">
            <LabelledValue label="Description: " value={item.description} />
          </p>
          <p className="menu-row__line t-p12">
            <LabelledValue label="Region: " value={item.region} />
          </p>
          <p className="menu-row__line t-p12">
            <span className="menu-row__more">Read More...</span>
          </p>
        </>
      )

    default:
      return null
  }
}

function LabelledValue({ label, value }) {
  return (
    <>
      <span className="menu-row__label">{label}</span>
      <span className="menu-row__value">{value}</span>
    </>
  )
}

// Selected allergens lead the list in red, as in the native cell; a lone diet's labels in gold.
function AllergenValue({ value, selectedAllergenIds }) {
  const label = <span className="menu-row__label">Allergens: </span>

  if (!selectedAllergenIds.length || !value || value === NA) {
    return (
      <>
        {label}
        <span className="menu-row__value">{value || NA}</span>
      </>
    )
  }

  const ordered = orderByMatch(splitAllergens(value), selectedAllergenIds)
  const hitClass = isDietSelection(selectedAllergenIds)
    ? "menu-row__diet-hit"
    : "menu-row__allergen-hit"

  return (
    <>
      {label}
      {ordered.map((part, index) => (
        <span key={`${part}-${index}`}>
          {index > 0 && <span className="menu-row__value">, </span>}
          <span
            className={
              isAllergenMatch(part, selectedAllergenIds) ? hitClass : "menu-row__value"
            }
          >
            {part}
          </span>
        </span>
      ))}
    </>
  )
}

function CategoryHeaderCard({ category }) {
  return (
    <div className="category-card">
      <RemoteImage
        className="category-card__image"
        src={category.thumbnailUrl || category.imageUrl}
        fallbackSrc={category.imageUrl}
        alt=""
      />
      <div className="category-card__body">
        <p className="category-card__title t-sb18">{category.name}</p>
        <p className="category-card__description t-p12">{category.description}</p>
      </div>
    </div>
  )
}
